import React from "react";
import { SovosBox, SovosStep, SovosStepButton, SovosStepLabel, SovosStepper, SovosTab, SovosTabs, SovosTypography } from "mosaic-react";
import { FLOW_STEP_INDEX, STEPS } from "../../store/views/MappingFlow/ColumnMappingCanvas/constants";

const StatusBadge = ({ label, dotColor, bgColor, borderColor, textColor }) => (
    <SovosBox
        sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 3,
            bgcolor: bgColor,
            border: "1px solid",
            borderColor,
        }}
    >
        <SovosBox sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: dotColor, flexShrink: 0 }} />
        <SovosTypography variant="caption" sx={{ color: textColor, fontWeight: 600, fontSize: 12 }}>
            {label}
        </SovosTypography>
    </SovosBox>
);

const ColumnMappingFlowHeader = ({
    step,
    frameworkName,
    imported,
    mappingPersisted = false,
    mappingLoading = false,
    actions = [],
    onStepClick,
    activeTab,
    onTabChange,
    showStagingDataTab = false,
    i18n = (strings) => (Array.isArray(strings) ? strings.join("") : strings),
}) => {
    const showHeader = step !== undefined;
    const showStatus = showHeader && !mappingLoading;
    const proceeded = Boolean(imported || mappingPersisted);

    const statusBadge = () => {
        if (proceeded) {
            return <StatusBadge label={i18n`Proceeded`} dotColor="success.main" bgColor="success.light" borderColor="success.light" textColor="success.dark" />;
        }
        return <StatusBadge label={i18n`Under mapping`} dotColor="warning.main" bgColor="warning.light" borderColor="warning.light" textColor="warning.dark" />;
    };

    if (!showHeader) return null;

    const showStepper = activeTab === "mapping" && !mappingLoading;
    const stepLabels = [i18n`Map entity`, i18n`Map columns`];

    return (
        <SovosBox
            sx={{
                borderBottom: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                px: 3,
                pt: 1.5,
                pb: 0,
            }}
        >
            <SovosTypography variant="caption" color="primary.main" fontWeight={600} sx={{ display: "block", mb: 0.5 }}>
                {i18n`Files`}
            </SovosTypography>

            <SovosBox sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, mb: 1.5 }}>
                <SovosBox sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
                    <SovosTypography variant="h4" fontWeight={700} noWrap>
                        {frameworkName || " "}
                    </SovosTypography>
                    {showStatus && statusBadge()}
                </SovosBox>

                {actions.length > 0 && (
                    <SovosBox sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1, flexShrink: 0 }}>
                        {actions.map((action) => action)}
                    </SovosBox>
                )}
            </SovosBox>

            {showStagingDataTab && (
                <SovosTabs
                    value={activeTab || "mapping"}
                    onChange={(_, value) => onTabChange && onTabChange(value)}
                    sx={{ mb: 1 }}
                >
                    <SovosTab value="mapping" label={i18n`Mapping`} />
                    <SovosTab value="staging" label={i18n`Staging Data`} />
                </SovosTabs>
            )}

            {showStepper && (
                <SovosStepper activeStep={step - FLOW_STEP_INDEX.ChooseEntity} sx={{ pb: 1.5, bgcolor: "transparent" }}>
                    {stepLabels.map((label, idx) => {
                        const stepNum = idx + 1;
                        const isDone = proceeded || stepNum < step;
                        return (
                            <SovosStep key={STEPS[idx]} completed={isDone}>
                                {isDone && onStepClick ? (
                                    <SovosStepButton onClick={() => onStepClick(stepNum)}>
                                        {label}
                                    </SovosStepButton>
                                ) : (
                                    <SovosStepLabel>{label}</SovosStepLabel>
                                )}
                            </SovosStep>
                        );
                    })}
                </SovosStepper>
            )}
        </SovosBox>
    );
};

export default ColumnMappingFlowHeader;
